// src/pages/ProfileGate/steps/BusinessBasicsStep.jsx

import React, {
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  OnboardingInterstitial,
} from "../OnboardingVisuals";

import {
  BUSINESS_SETUP_OPTIONS,
  TRADING_DURATION_OPTIONS,
  EMPLOYEE_COUNT_OPTIONS_V3,
  INDUSTRY_OPTIONS_V3,
  PROVINCE_OPTIONS_V3,
  useProfileGateDraft,
} from "../../../hooks/useProfileGateDraft";

import {
  saveOnboardingDraftProgress,
} from "../../../lib/onboarding/profileGate";

const BUSINESS_PENGUIN_URL =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Onboarding/Gwin_Book.png";

function SelectField({
  label,
  value,
  options,
  placeholder,
  onChange,
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-[14px] font-semibold text-[#333333]">
        {label}
      </span>

      <div className="relative">
        <select
          value={value || ""}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          className={[
            "w-full appearance-none rounded-[16px] border-2 bg-white",
            "px-4 py-3.5 pr-11",
            "text-[15px] font-medium text-[#333333]",
            "outline-none transition",
            "border-[#E1E1E1]",
            "focus:border-[#FF914D]",
          ].join(" ")}
        >
          <option value="">
            {placeholder}
          </option>

          {options.map(
            (option) => {
              const item =
                typeof option ===
                "string"
                  ? {
                      label:
                        option,
                      value:
                        option,
                    }
                  : option;

              return (
                <option
                  key={
                    item.value
                  }
                  value={
                    item.value
                  }
                >
                  {
                    item.label
                  }
                </option>
              );
            }
          )}
        </select>

        <div
          className="
            pointer-events-none
            absolute
            right-4
            top-1/2
            -translate-y-1/2
            text-[#757575]
          "
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </label>
  );
}

export default function BusinessBasicsStep() {
  const navigate =
    useNavigate();

  const {
    loading,
    draft,
    setField,
  } =
    useProfileGateDraft();

  const [
    saving,
    setSaving,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  if (loading) {
    return null;
  }

  const canContinue =
    Boolean(
      String(
        draft.business_name ||
          ""
      ).trim() &&
        draft.business_setup_type &&
        draft.trading_duration_range &&
        draft.employee_count_range &&
        draft.industry &&
        draft.province
    );

  const handleFieldChange = (
    field,
    value
  ) => {
    setError("");

    setField(
      field,
      value
    );
  };

  const handleContinue =
    async () => {
      if (
        !canContinue ||
        saving
      ) {
        return;
      }

      try {
        setSaving(true);
        setError("");

        await saveOnboardingDraftProgress(
          {
            ...draft,

            business_name:
              String(
                draft.business_name ||
                  ""
              ).trim(),

            business_setup_type:
              draft.business_setup_type,

            trading_duration_range:
              draft.trading_duration_range,

            employee_count_range:
              draft.employee_count_range,

            industry:
              draft.industry,

            province:
              draft.province,
          },
          {
            resumeStep:
              "financial-setup",
          }
        );

        navigate(
          "/profile-gate/financial-setup"
        );
      } catch (err) {
        console.error(
          "Could not save business basics:",
          err
        );

        setError(
          err?.message ||
            "Could not save your business details. Please try again."
        );
      } finally {
        setSaving(false);
      }
    };

  return (
    <OnboardingInterstitial
      message="Tell me a little about your business."
      secondaryMessage=""
      showBack={true}
      showSpeechPenguin={true}
      speechPenguinUrl={
        BUSINESS_PENGUIN_URL
      }
      speechPenguinClassName="
        w-[105px]
        sm:w-[125px]
        md:w-[140px]
      "
      nextLabel={
        saving
          ? "Saving..."
          : "Continue"
      }
      nextDisabled={
        !canContinue ||
        saving
      }
      onBack={() =>
        navigate(
          "/profile-gate/focus"
        )
      }
      onNext={
        handleContinue
      }
    >
      <div className="mx-auto mt-7 w-full max-w-[520px]">
        <div className="space-y-5">
          <label className="block">
            <span className="mb-2 block text-[14px] font-semibold text-[#333333]">
              What’s your business called?
            </span>

            <input
              type="text"
              value={
                draft.business_name ||
                ""
              }
              onChange={(
                event
              ) =>
                handleFieldChange(
                  "business_name",
                  event.target
                    .value
                )
              }
              placeholder="Business name"
              className="
                w-full
                rounded-[16px]
                border-2
                border-[#E1E1E1]
                bg-white
                px-4
                py-3.5
                text-[15px]
                font-medium
                text-[#333333]
                outline-none
                transition
                placeholder:text-[#A0A0A0]
                focus:border-[#FF914D]
              "
            />
          </label>

          <SelectField
            label="How is your business set up?"
            value={
              draft.business_setup_type
            }
            options={
              BUSINESS_SETUP_OPTIONS
            }
            placeholder="Select an option"
            onChange={(
              value
            ) =>
              handleFieldChange(
                "business_setup_type",
                value
              )
            }
          />

          <SelectField
            label="How long have you been trading?"
            value={
              draft.trading_duration_range
            }
            options={
              TRADING_DURATION_OPTIONS
            }
            placeholder="Select an option"
            onChange={(
              value
            ) =>
              handleFieldChange(
                "trading_duration_range",
                value
              )
            }
          />

          <SelectField
            label="How many people work in the business, including you?"
            value={
              draft.employee_count_range
            }
            options={
              EMPLOYEE_COUNT_OPTIONS_V3
            }
            placeholder="Select an option"
            onChange={(
              value
            ) =>
              handleFieldChange(
                "employee_count_range",
                value
              )
            }
          />

          <SelectField
            label="What industry are you in?"
            value={
              draft.industry
            }
            options={
              INDUSTRY_OPTIONS_V3
            }
            placeholder="Select an industry"
            onChange={(
              value
            ) =>
              handleFieldChange(
                "industry",
                value
              )
            }
          />

          <SelectField
            label="Where are you based?"
            value={
              draft.province
            }
            options={
              PROVINCE_OPTIONS_V3
            }
            placeholder="Select a province"
            onChange={(
              value
            ) =>
              handleFieldChange(
                "province",
                value
              )
            }
          />
        </div>

        {error ? (
          <p className="mt-4 text-center text-sm font-medium text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </OnboardingInterstitial>
  );
}